import { isAffirmativeConfirm } from "./commandIntent";
import { INVITE_ROLES } from "./inviteConstants";
import { ASSIGN_ALL_VALUE } from "./ProfileAssignmentForm";
import {
  defaultBlocksForBase,
  isTeamReportBase,
  PROFILE_ROLES,
} from "./reportProfileDefaults";

export const WORKFLOW_GUIDE_IDS = ["invite", "reminder", "exec", "team", "company", "profile", "lite", "pe"];

export const REPORT_FORM_GUIDE_IDS = ["exec", "team", "company", "lite"];

export const MULTI_COMPANY_GUIDE_IDS = ["pe"];

export const PE_REPORT_MIN_COMPANIES = 2;
export const PE_REPORT_MAX_COMPANIES = 4;

export function guideUsesCompanySelect(guideId) {
  return REPORT_FORM_GUIDE_IDS.includes(guideId);
}

export function guideUsesMultiCompanySelect(guideId) {
  return MULTI_COMPANY_GUIDE_IDS.includes(guideId);
}

export function guideUsesProfileCompanySelect(guideId) {
  return guideId === "profile";
}

function parseEmails(raw) {
  return String(raw || "")
    .split(/[,;\n]+/)
    .map((e) => e.trim())
    .filter((e) => e.includes("@"));
}

export function parseCompaniesList(raw) {
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c || "").trim()).filter(Boolean);
  }
  return String(raw || "")
    .split(/[,;\n]+/)
    .map((c) => c.trim())
    .filter(Boolean);
}

export const WORKFLOW_GUIDES = {
  invite: {
    id: "invite",
    title: "Send invites",
    askPrompt:
      "Who should receive invites? Type their email addresses, the company name, and role if everyone shares the same role — then press Go.",
    inputPlaceholder:
      "e.g. Invite jane@acme.com and john@acme.com to Acme Corp as Leadership Team",
    intro:
      "Enter participant email addresses, the company they belong to, and optionally a role for everyone in this batch.",
    submitLabel: "Send to planner",
    fields: [
      {
        key: "emails",
        label: "Email addresses",
        type: "textarea",
        placeholder: "jane@acme.com, john@acme.com",
        hint: "Separate multiple emails with commas or new lines",
        required: true,
      },
      {
        key: "company",
        label: "Company name",
        type: "text",
        placeholder: "Acme Corp",
        required: true,
      },
      {
        key: "role",
        label: "Role (optional)",
        type: "select",
        placeholder: "Choose if same role for everyone",
        options: [{ value: "", label: "Choose per participant later" }, ...INVITE_ROLES.map((r) => ({ value: r, label: r }))],
        required: false,
      },
    ],
  },
  reminder: {
    id: "reminder",
    title: "Draft reminder",
    askPrompt:
      "Draft reminder emails for participants who opened but have not completed. Add a company name to narrow scope, then press Go.",
    inputPlaceholder: "Optional: Acme Corp",
    intro:
      "Uses Mail Manager resend templates. Only in-progress (opened) invites — not “not started.” You confirm before anything is sent.",
    submitLabel: "Draft plan",
    fields: [
      {
        key: "company",
        label: "Company (optional)",
        type: "text",
        placeholder: "Leave blank for all companies in your scope",
        required: false,
      },
    ],
  },
  exec: {
    id: "exec",
    title: "Executive summary",
    askPrompt:
      "Which company and perspective role should the executive summary use? Type both in one line, then press Go.",
    inputPlaceholder: "e.g. Open executive summary for Acme Corp as CEO",
    intro: "Enter the company and the perspective role for the executive summary PDF.",
    submitLabel: "Open workflow",
    fields: [
      {
        key: "company",
        label: "Company",
        type: "companySelect",
        placeholder: "Select a company",
        hint: "Report-ready companies with completed assessment data",
        required: true,
      },
      {
        key: "role",
        label: "Perspective role",
        type: "select",
        options: INVITE_ROLES.map((r) => ({ value: r, label: r })),
        required: true,
      },
    ],
  },
  team: {
    id: "team",
    title: "Team report",
    askPrompt: "Which company’s team report should I open? Type the company name, then press Go.",
    inputPlaceholder: "e.g. Open team report for Acme Corp",
    intro: "Enter the company name for the team assessment report you want to open.",
    submitLabel: "Open workflow",
    fields: [
      {
        key: "company",
        label: "Company",
        type: "companySelect",
        placeholder: "Select a company",
        hint: "Report-ready companies with completed assessment data",
        required: true,
      },
    ],
  },
  profile: {
    id: "profile",
    title: "Report profile",
    askPrompt:
      "Describe the profile — team or company base, name, target role, blocks in order, and optionally assign to one company or all. Press Go when ready.",
    inputPlaceholder:
      "e.g. Create company report profile for Board Member named Board Pack for Acme Corp: intro, pillars, gaps, variance — assign to Acme Corp",
    intro:
      "Say team or company base, profile name, audience role, block list, and which company to assign to (or all companies).",
    submitLabel: "Create profile",
    fields: [
      {
        key: "reportBase",
        label: "Report base",
        type: "select",
        required: true,
        options: [
          { value: "company", label: "Company report" },
          { value: "team", label: "Team report" },
        ],
      },
      {
        key: "name",
        label: "Profile name",
        type: "text",
        placeholder: "e.g. Board Pack, Leadership Summary",
        required: true,
      },
      {
        key: "targetRole",
        label: "Target role",
        type: "select",
        required: false,
        options: [
          { value: "", label: "Any role (default)" },
          ...PROFILE_ROLES.filter(Boolean).map((role) => ({ value: role, label: role })),
        ],
      },
      {
        key: "company",
        label: "Assign to company",
        type: "profileCompanySelect",
        placeholder: "All companies",
        hint: "Leave as all companies, or pick one to scope this profile",
        required: false,
      },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Optional notes about when to use this profile",
        required: false,
      },
      {
        key: "blocks",
        label: "Report blocks",
        type: "profileBlocks",
        required: true,
        hint: "Enable blocks, drag to reorder, or use arrows on mobile",
      },
    ],
  },
  company: {
    id: "company",
    title: "Company report",
    askPrompt: "Which company’s report should I open? Type the company name, then press Go.",
    inputPlaceholder: "e.g. Open company report for Acme Corp",
    intro: "Enter the company name for the company-wide assessment report you want to open.",
    submitLabel: "Open workflow",
    fields: [
      {
        key: "company",
        label: "Company",
        type: "companySelect",
        placeholder: "Select a company",
        hint: "Report-ready companies with completed assessment data",
        required: true,
      },
    ],
  },
  lite: {
    id: "lite",
    title: "Lite report",
    askPrompt: "Which company’s lite report should I open? Type the company name, then press Go.",
    inputPlaceholder: "e.g. Open lite report for Acme Corp",
    intro: "Select a company with a completed LITE assessment to open the lite company report PDF.",
    submitLabel: "Open lite report",
    fields: [
      {
        key: "company",
        label: "Company",
        type: "companySelect",
        placeholder: "Select a company",
        hint: "Companies with a completed LITE assessment",
        required: true,
      },
    ],
  },
  pe: {
    id: "pe",
    title: "PE portfolio report",
    askPrompt:
      "Which companies should the portfolio roll-up cover? Pick 2–4 companies, then press Go.",
    inputPlaceholder: "e.g. Portfolio report for Acme Corp, Beta Inc, and Gamma LLC",
    intro:
      "Select between 2 and 4 report-ready companies to generate the same portfolio roll-up PDF as Insights.",
    submitLabel: "Download report (with charts)",
    secondarySubmitLabel: "Open in Insights",
    fields: [
      {
        key: "companies",
        label: "Companies",
        type: "companiesMulti",
        placeholder: "Select companies",
        hint: "Choose 2–4 report-ready companies",
        required: true,
        min: PE_REPORT_MIN_COMPANIES,
        max: PE_REPORT_MAX_COMPANIES,
      },
    ],
  },
};

export function emptyGuideValues(guideId) {
  const guide = WORKFLOW_GUIDES[guideId];
  if (!guide) return {};
  return Object.fromEntries(
    guide.fields.map((f) => {
      if (f.type === "companiesMulti") return [f.key, []];
      if (f.type === "profileBlocks") return [f.key, defaultBlocksForBase("company")];
      if (f.type === "profileCompanySelect") return [f.key, ASSIGN_ALL_VALUE];
      if (f.key === "reportBase") return [f.key, "company"];
      return [f.key, ""];
    })
  );
}

const ROLE_NAMES_PATTERN = "CEO|Leadership Team|Board Member|PE Sponsor";

/** Prefill workflow form fields when the user typed a workflow in the command bar. */
export function parseWorkflowGuideValuesFromText(guideId, text) {
  const base = emptyGuideValues(guideId);
  const raw = String(text || "").trim();
  if (!raw || !guideId) return base;

  const stripLead = (pattern) => {
    const m = raw.match(pattern);
    return m ? m[1].trim() : "";
  };

  switch (guideId) {
    case "reminder": {
      const atMatch = raw.match(
        /opened\s+but\s+have\s+not\s+completed(?:\s+at|\s+for)?\s+(.+)$/i
      );
      if (atMatch) base.company = atMatch[1].trim();
      else if (!/draft\s+reminder|opened\s+but/i.test(raw) && raw) base.company = raw;
      return base;
    }
    case "invite": {
      const emails = parseEmails(raw);
      if (emails.length) base.emails = emails.join(", ");
      const toMatch = raw.match(/\bto\s+(.+?)(?:\s+as\s+|$)/i);
      if (toMatch) base.company = toMatch[1].replace(/\s+as\s+.+$/i, "").trim();
      const roleMatch = raw.match(new RegExp(`\\bas\\s+(${ROLE_NAMES_PATTERN})\\s*$`, "i"));
      if (roleMatch) base.role = roleMatch[1];
      return base;
    }
    case "exec": {
      const m = raw.match(
        new RegExp(
          `(?:executive\\s+summary|exec\\s+summary)\\s+(?:for\\s+)?(.+?)(?:\\s+as\\s+(${ROLE_NAMES_PATTERN}))?\\s*$`,
          "i"
        )
      );
      if (m) {
        base.company = (m[1] || "").trim();
        if (m[2]) base.role = m[2];
      }
      return base;
    }
    case "team":
      base.company = stripLead(/team\s+report\s+(?:for\s+)?(.+)$/i);
      return base;
    case "company":
      base.company = stripLead(/company\s+report\s+(?:for\s+)?(.+)$/i);
      return base;
    case "lite":
      base.company = stripLead(/lite\s+report\s+(?:for\s+)?(.+)$/i);
      return base;
    case "pe": {
      const listMatch = raw.match(/(?:portfolio\s+report|portfolio\s+roll-?up|pe\s+report)\s+(?:for\s+)?(.+)$/i);
      const tail = listMatch ? listMatch[1] : raw;
      const companies = parseCompaniesList(tail.replace(/^open\s+/i, ""));
      if (companies.length) base.companies = companies.slice(0, PE_REPORT_MAX_COMPANIES);
      return base;
    }
    case "profile": {
      if (/\bteam\s+(?:report\s+)?profile\b/i.test(raw)) base.reportBase = "team";
      else if (/\bcompany\s+(?:report\s+)?profile\b/i.test(raw)) base.reportBase = "company";
      base.blocks = defaultBlocksForBase(base.reportBase || "company");
      const roleMatch = raw.match(new RegExp(`\\b(${ROLE_NAMES_PATTERN})\\b`, "i"));
      if (roleMatch) base.targetRole = roleMatch[1];
      const namedMatch = raw.match(/\b(?:named|called)\s+([^:,]+)/i);
      if (namedMatch) base.name = namedMatch[1].trim();
      const assignMatch = raw.match(
        /\b(?:assign(?:ed)?\s+to|for\s+company|apply\s+to|scoped\s+to)\s+([^,.]+?)(?:\s+only|\s*$|,)/i
      );
      if (assignMatch) {
        const candidate = assignMatch[1].trim();
        if (candidate && !/\ball\b/i.test(candidate)) base.company = candidate;
      }
      if (/\b(?:all|every)\s+compan/i.test(raw)) base.company = ASSIGN_ALL_VALUE;
      const stripped = raw.replace(/^create\s+(?:team|company)?\s*report\s+profile:?\s*/i, "").trim();
      if (stripped && !base.name) base.description = stripped;
      return base;
    }
    default:
      return base;
  }
}

export function validateGuideValues(guideId, values) {
  const guide = WORKFLOW_GUIDES[guideId];
  if (!guide) return { ok: false, error: "Unknown workflow" };

  for (const field of guide.fields) {
    if (!field.required) continue;
    if (field.type === "companiesMulti") {
      const companies = parseCompaniesList(values[field.key]);
      if (!companies.length) {
        return { ok: false, error: `${field.label} is required` };
      }
      continue;
    }
    if (field.type === "profileBlocks") {
      const blocks = Array.isArray(values[field.key]) ? values[field.key] : [];
      if (!blocks.some((b) => b.enabled !== false)) {
        return { ok: false, error: "At least one report block must be enabled" };
      }
      continue;
    }
    const v = String(values[field.key] ?? "").trim();
    if (!v) {
      return { ok: false, error: `${field.label} is required` };
    }
  }

  if (guideId === "invite") {
    const emails = parseEmails(values.emails);
    if (!emails.length) {
      return { ok: false, error: "Enter at least one valid email address" };
    }
  }

  if (guideId === "pe") {
    const companies = parseCompaniesList(values.companies);
    if (companies.length < PE_REPORT_MIN_COMPANIES) {
      return { ok: false, error: `Select at least ${PE_REPORT_MIN_COMPANIES} companies` };
    }
    if (companies.length > PE_REPORT_MAX_COMPANIES) {
      return { ok: false, error: `Select up to ${PE_REPORT_MAX_COMPANIES} companies` };
    }
  }

  return { ok: true };
}

/** Turn free-text command-bar replies into planner-friendly workflow instructions. */
export function buildWorkflowMessageFromChat(guideId, text) {
  const raw = String(text || "").trim();
  if (!raw || !guideId) return raw;

  const parseCompanyAndRole = () => {
    const asMatch = raw.match(
      new RegExp(`^(.+?)\\s+(?:as|for)\\s+(${ROLE_NAMES_PATTERN})\\s*$`, "i")
    );
    if (asMatch) return { company: asMatch[1].trim(), role: asMatch[2] };
    const andMatch = raw.match(
      new RegExp(`^(.+?)\\s+and\\s+(${ROLE_NAMES_PATTERN})\\s*$`, "i")
    );
    if (andMatch) return { company: andMatch[1].trim(), role: andMatch[2] };
    const commaMatch = raw.match(
      new RegExp(`^(.+?),\\s*(${ROLE_NAMES_PATTERN})\\s*$`, "i")
    );
    if (commaMatch) return { company: commaMatch[1].trim(), role: commaMatch[2] };
    return { company: raw, role: "" };
  };

  switch (guideId) {
    case "reminder": {
      if (isAffirmativeConfirm(raw)) return raw;
      if (/draft\s+reminder|opened\s+but\s+have\s+not\s+completed/i.test(raw)) return raw;
      const company = raw.trim();
      if (!company) {
        return "Draft reminder emails for participants who opened but have not completed";
      }
      return `Draft reminder emails for participants who opened but have not completed at ${company}`;
    }
    case "exec": {
      if (/executive\s+summary|exec\s+summary/i.test(raw)) return raw;
      const { company, role } = parseCompanyAndRole();
      if (company && role) return `Open executive summary for ${company} as ${role}`;
      return `Open executive summary for ${company || raw}`;
    }
    case "team": {
      if (/team\s+report/i.test(raw)) return raw;
      const { company } = parseCompanyAndRole();
      return `Open team report for ${company || raw}`;
    }
    case "company": {
      if (/company\s+report/i.test(raw)) return raw;
      const { company } = parseCompanyAndRole();
      return `Open company report for ${company || raw}`;
    }
    case "lite": {
      if (/lite\s+report/i.test(raw)) return raw;
      const { company } = parseCompanyAndRole();
      return `Open lite report for ${company || raw}`;
    }
    case "pe": {
      if (/portfolio\s+report|portfolio\s+roll-?up/i.test(raw)) return raw;
      return raw.toLowerCase().startsWith("open") ? raw : `Open portfolio report for ${raw}`;
    }
    case "profile": {
      if (/report\s+profile/i.test(raw)) return raw;
      return raw.toLowerCase().startsWith("create") ? raw : `Create report profile: ${raw}`;
    }
    case "invite":
    default:
      return raw;
  }
}

export function buildWorkflowInstruction(guideId, values) {
  const company = String(values.company ?? "").trim();
  const role = String(values.role ?? "").trim();

  switch (guideId) {
    case "invite": {
      const emails = parseEmails(values.emails);
      if (!emails.length || !company) return null;
      const list = emails.join(", ");
      return role ? `Invite ${list} to ${company} as ${role}` : `Invite ${list} to ${company}`;
    }
    case "reminder":
      if (company) {
        return `Draft reminder emails for participants who opened but have not completed at ${company}`;
      }
      return "Draft reminder emails for participants who opened but have not completed";
    case "exec":
      if (!company || !role) return null;
      return `Open executive summary for ${company} as ${role}`;
    case "team":
      if (!company) return null;
      return `Open team report for ${company}`;
    case "company":
      if (!company) return null;
      return `Open company report for ${company}`;
    case "lite":
      if (!company) return null;
      return `Open lite report for ${company}`;
    case "pe": {
      const companies = parseCompaniesList(values.companies);
      if (companies.length < PE_REPORT_MIN_COMPANIES) return null;
      return `Open portfolio report for ${companies.join(", ")}`;
    }
    case "profile": {
      const name = String(values.name ?? "").trim();
      const reportBase = String(values.reportBase ?? "company").trim();
      const targetRole = String(values.targetRole ?? "").trim();
      const description = String(values.description ?? "").trim();
      const companyValue = String(values.company ?? ASSIGN_ALL_VALUE).trim();
      const blocks = Array.isArray(values.blocks) ? values.blocks : [];
      if (!name) return null;
      const baseLabel = isTeamReportBase(reportBase) ? "team" : "company";
      const enabled = blocks
        .filter((b) => b.enabled !== false)
        .sort((a, b) => a.order - b.order)
        .map((b) => b.block_id.replace(/_/g, " "))
        .join(", ");
      const assignPart =
        companyValue && companyValue !== ASSIGN_ALL_VALUE
          ? `assign to ${companyValue}`
          : "assign to all companies";
      const parts = [
        `Create ${baseLabel} report profile named ${name}`,
        targetRole ? `for ${targetRole}` : "",
        assignPart,
        enabled ? `with blocks: ${enabled}` : "",
        description ? `— ${description}` : "",
      ].filter(Boolean);
      return parts.join(" ");
    }
    default:
      return null;
  }
}

export function getWorkflowGuide(guideId) {
  return WORKFLOW_GUIDES[guideId] || null;
}
